import { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import UnsavedChangesModal from "./UnsavedChangesModal";

const NavGuardContext = createContext(null);

export const NavigationGuardProvider = ({ children }) => {
  const navigate = useNavigate();
  const guardsRef = useRef(new Map());
  const pendingActionRef = useRef(null); // () => void after user picks save/discard
  const [promptOpen, setPromptOpen] = useState(false);

  const register = useCallback((key, payload) => {
    guardsRef.current.set(key, payload);
    // No forceRender needed - guards are checked on-demand via getDirty()
  }, []);
  const unregister = useCallback((key) => {
    guardsRef.current.delete(key);
    // No forceRender needed - guards are checked on-demand via getDirty()
  }, []);

  const getDirty = () => {
    for (const g of guardsRef.current.values()) {
      if (g?.isDirty) return g;
    }
    return null;
  };

  const guardedNavigate = useCallback((path, opts) => {
    if (getDirty()) {
      pendingActionRef.current = () => navigate(path, opts);
      setPromptOpen(true);
      return;
    }
    navigate(path, opts);
  }, [navigate]);

  /**
   * Trigger the unsaved-changes prompt for an arbitrary action (e.g., closing an editor).
   * If not dirty, runs `afterResolve` immediately. Otherwise, opens modal and runs
   * the chosen side-effect + `afterResolve`.
   */
  const promptAction = useCallback((afterResolve) => {
    if (!getDirty()) {
      afterResolve && afterResolve();
      return;
    }
    pendingActionRef.current = afterResolve || (() => {});
    setPromptOpen(true);
  }, []);

  const resolve = useCallback((choice) => {
    setPromptOpen(false);
    const dirtyGuard = getDirty();
    const next = pendingActionRef.current;
    pendingActionRef.current = null;
    if (choice === "stay") return;
    if (choice === "save" && dirtyGuard?.onSaveDraft) dirtyGuard.onSaveDraft();
    if (choice === "discard" && dirtyGuard?.onDiscard) dirtyGuard.onDiscard();
    if (next) setTimeout(next, 0);
  }, []);

  useEffect(() => {
    const h = (e) => { if (getDirty()) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);

  const value = { register, unregister, guardedNavigate, promptAction };

  return (
    <NavGuardContext.Provider value={value}>
      {children}
      <UnsavedChangesModal open={promptOpen} onConfirm={resolve} />
    </NavGuardContext.Provider>
  );
};

export const useNavGuard = () => useContext(NavGuardContext);

export const useGuardedNavigate = () => {
  const ctx = useContext(NavGuardContext);
  const plain = useNavigate();
  return ctx?.guardedNavigate || plain;
};
